import { MapPolygonSeries } from '@amcharts/amcharts4/maps';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import * as d3 from 'd3';

const EUROPE = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE"
];

@Component({
  selector: 'app-graph-documents-map',
  templateUrl: './graph-documents-map.component.html',
  styleUrls: ['./graph-documents-map.component.scss']
})
export class GraphDocumentsMapComponent {
  @Input() data: {
    _id: string,
    count: number
  }[] = [];

  @Input() loading: boolean = true;

  @Input() title: boolean = true;

  @Input() height: number = 400;
  @Input() count: number = 0;

  @Input() center?: string;

  @Input() main?: string;

  ready: boolean = false;

  @Input() error: boolean = false;
  @Input() noData: boolean = false;

  @ViewChild('graphclassdiv') chartElement?: ElementRef<HTMLElement>;
  private chart?: any;

  top5: {
    id: string,
    value: number,
    percent: number
  }[] = []

  constructor(
    private zone: NgZone,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef
  ) {

  }



  ngAfterViewInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.loading && changes['data']) {
      setTimeout(() => {
        this.makeChart();
      }, 100)
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }

  makeChart() {
    const self = this;
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {


        Promise.all([
          import(/* webpackChunkName: "am4core" */ '@amcharts/amcharts4/core'),
          import(/* webpackChunkName: "am4maps" */ '@amcharts/amcharts4/maps'),
          import(/* webpackChunkName: "am4geodata_worldLow" */ '@amcharts/amcharts4-geodata/worldHigh'),
        ]
        ).then((modules) => {
          const am4core = modules[0];
          const am4maps = modules[1];
          const am4geodata_worldLow = modules[2];


          let chart = am4core.create("graphterritorydiv", am4maps.MapChart);
          chart.logo.setVisibility(false);
          // Set map definition
          chart.geodata = am4geodata_worldLow.default;
          chart.preloader!.hiddenState.transitionDuration = 0;

          // Set projection
          chart.projection = new am4maps.projections.Miller();

          // Create map polygon series
          var polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());

          // Exclude Antartica
          polygonSeries.exclude = ["AQ"];

          // Make map load polygon (like country names) data from GeoJSON
          polygonSeries.useGeodata = true;

          let filteredData = this.data.filter(d => !["EU", "WO", "ASI", "AFR"].includes(d._id)).map((datum) => ({ id: datum._id, value: datum.count }))
          const minValue = Math.min(...filteredData.map(d => d.value));
          const maxValue = Math.max(...filteredData.map(d => d.value));
          const colorScale = d3
            .scaleLinear([
              minValue,
              maxValue
            ], [
              "#f3ecfc",
              "#8847E3"
            ]);
          filteredData = filteredData.map(datum => {
            let hasColor = false;
            if (this.main && this.main !== 'EU' && this.main === datum.id) {
              hasColor = true;
            } else if (this.main && this.main !== 'EU' && EUROPE.includes(datum.id)) {
              hasColor = true;
            } else if (!this.main) {
              hasColor = true;
            }
            return {
              ...datum,
              color: !hasColor ? am4core.color("#747479") : am4core.color(colorScale(datum.value))
            }
          })



          polygonSeries.data = filteredData;
          this.top5 = filteredData.sort((a, b) => b.value - a.value).slice(0, 5).map((item) => ({ ...item, percent: Math.round(item.value * 100 / this.count) }));
          this.changeDetector.detectChanges();

          // polygonSeries.heatRules.push({
          //   property: "fill",
          //   target: polygonSeries.mapPolygons.template,
          //   min: am4core.color("#f3ecfc"),
          //   max: am4core.color("#8847E3"),
          // });

          // Configure series
          var polygonTemplate = polygonSeries.mapPolygons.template;

          polygonTemplate.fill = am4core.color("#f5f5f7");
          polygonTemplate.propertyFields.fill = "color";

          // Create hover state and set alternative fill color
          // let as = polygonTemplate.states.create("active");
          // as.properties.fill = am4core.color("#8847e3");

          // let heatLegend = chart.createChild(am4maps.HeatLegend);
          // heatLegend.series = polygonSeries;
          // heatLegend.align = "right";
          // heatLegend.valign = "bottom";
          // heatLegend.width = am4core.percent(20);
          // heatLegend.marginRight = am4core.percent(4);
          // heatLegend.minValue = Math.min(...filteredData.map(d => d.value));
          // heatLegend.maxValue = Math.max(...filteredData.map(d => d.value));
          // heatLegend.valueAxis.renderer.labels.template.fontSize = 10;
          // heatLegend.valueAxis.renderer.labels.template.fill = am4core.color("#747479");
          // heatLegend.valueAxis.renderer.labels.template.fontWeight = 'bold';
          // heatLegend.valueAxis.renderer.labels.template.fontFamily = 'Source Sans 3, sans-serif';

          // // Set up custom heat map legend labels using axis ranges
          // var minRange = heatLegend.valueAxis.axisRanges.create();
          // minRange.value = heatLegend.minValue;
          // minRange.label.text = Math.min(...filteredData.map(d => d.value)).toString();
          // var maxRange = heatLegend.valueAxis.axisRanges.create();
          // maxRange.value = heatLegend.maxValue;
          // maxRange.label.text = Math.max(...filteredData.map(d => d.value)).toString();

          // // Blank out internal heat legend value axis labels
          // heatLegend.valueAxis.renderer.labels.template.adapter.add("text", function (labelText) {
          //   return "";
          // });

          polygonSeries.mapPolygons.template.adapter.add("tooltipText", function (text, target) {
            if (!(target.dataItem.dataContext as any).value || (target.dataItem.dataContext as any).value === 0) {
              return "";
            }
            return `${text} ${self.translate.instant('ENTITIES.DOCUMENTS').toLowerCase()}`

          });


          // Configure series tooltip
          var polygonTemplate = polygonSeries.mapPolygons.template;
          polygonTemplate.tooltipText = "{name}: {value}";
          polygonTemplate.nonScalingStroke = true;
          polygonTemplate.strokeWidth = 0.5;

          this.chart = chart;
          this.chart.events.on('ready', () => {
            this.ready = true;
            if (!this.center) {
              this.zoomToCountries(filteredData, polygonSeries);
            } else if (this.center) {
              if (this.center === 'EU' || this.center === 'EM') {
                this.zoomToCountries(filteredData, polygonSeries, EUROPE);
              } else {
                this.zoomToCountry(this.center, polygonSeries);
              }

            }

          });
        })
      }, 100);

    })

  }

  zoomToCountries(data: {
    id: string,
    value: number
  }[], series: MapPolygonSeries, restriction?: string[]) {
    var north, south, west, east;
    if (data && data.length > 0) {
      for (let i = 0; i < data.length; i++) {
        if (data[i].value > 0 && (!restriction || (restriction && restriction.includes(data[i].id)))) {
          var country = series.getPolygonById(data[i].id);
          if (north == undefined || (country.north > north)) {
            north = country.north;
          }
          if (south == undefined || (country.south < south)) {
            south = country.south;
          }
          if (west == undefined || (country.west < west)) {
            west = country.west;
          }
          if (east == undefined || (country.east > east)) {
            east = country.east;
          }
        }
      }
      this.chart.zoomToRectangle(north as number, east as number, south as number, west as number, 1, true);
    }
  }

  zoomToCountry(id: string, series: MapPolygonSeries) {
    console.log(id)
    console.log(series.getPolygonById(id.toUpperCase()))
    console.log(series.getPolygonById("USA"))
    this.chart.zoomToMapObject(series.getPolygonById(id.toUpperCase()));
  }
}
